const cleanMarkdownLinks = (text) => text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

const SECTION_ORDER = ["mejoras", "correcciones", "rendimiento", "seguridad", "otros"];
const SECTION_LABELS = {
    mejoras: "Mejoras",
    correcciones: "Correcciones",
    rendimiento: "Rendimiento",
    seguridad: "Seguridad",
    otros: "Otros cambios",
};

const INTERNAL_PATTERNS = [
    /\b(test|tests|testing|pytest|jest|coverage)\b/i,
    /\b(refactor|cleanup|lint|prettier|eslint|format)\b/i,
    /\b(ci|cd|pipeline|workflow|github actions|hook)\b/i,
    /\b(chore|deps|dependency|dependencies|build)\b/i,
    /\bmerge\b/i,
    /^bump\b/i,
    /^release\s+please\b/i,
];

const CATEGORY_PATTERNS = {
    correcciones: [
        /\bfix\b/i,
        /\bbug\b/i,
        /\berror\b/i,
        /\bfail\b/i,
        /\bcorreg/i,
        /\bsolucion/i,
        /\bfallo/i,
    ],
    rendimiento: [
        /\bperformance\b/i,
        /\brendimiento\b/i,
        /\boptim/i,
        /\bvelocidad\b/i,
        /\bcache\b/i,
        /\bmejora de tiempos\b/i,
    ],
    seguridad: [
        /\bsecurity\b/i,
        /\bseguridad\b/i,
        /\bvulnerab/i,
        /\btoken\b/i,
        /\bpermiso/i,
    ],
    mejoras: [
        /\bfeat\b/i,
        /\bfeature\b/i,
        /\bnuevo\b/i,
        /\bnueva\b/i,
        /\bmejora\b/i,
        /\badd\b/i,
        /\bagreg/i,
        /\bañad/i,
        /\binterfaz\b/i,
        /\bux\b/i,
        /\bui\b/i,
        /\bexperiencia\b/i,
    ],
};

const cleanTechnicalNoise = (text) => {
    return text
        .replace(/\s+by\s+@[-\w]+/gi, "")
        .replace(/\s+in\s+https?:\/\/\S+/gi, "")
        .replace(/\s+in\s+#\d+/gi, "")
        .replace(/https?:\/\/\S+/gi, "")
        .replace(/\s+\(#\d+\)/g, "")
        .replace(/`/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();
};

const normalizeLine = (line) => {
    const withoutHeading = line.replace(/^#+\s*/, "").trim();
    const withoutBullet = withoutHeading.replace(/^[-*]\s+/, "").trim();
    return cleanTechnicalNoise(cleanMarkdownLinks(withoutBullet));
};

const shouldHideForUser = (line) => {
    if (!line || line.length < 4) return true;
    return INTERNAL_PATTERNS.some((pattern) => pattern.test(line));
};

const getCategory = (line) => {
    for (const [category, patterns] of Object.entries(CATEGORY_PATTERNS)) {
        if (patterns.some((pattern) => pattern.test(line))) {
            return category;
        }
    }
    return "otros";
};

export const formatReleaseTitle = (release) => {
    if (!release) return "Novedades";
    const title = (release.title || "").trim();
    const version = (release.version || "").trim();

    if (!title && version) return `Versión ${version}`;
    if (/^v?\d+\.\d+\.\d+$/i.test(title) && version) return `Versión ${version}`;
    return title || `Versión ${version}`;
};

export const parseReleaseDescription = (description) => {
    const result = {
        highlights: [],
        notes: [],
        fullChangelogUrl: "",
        sections: [],
        hiddenInternalCount: 0,
    };

    if (!description || typeof description !== "string") {
        return result;
    }

    const lines = description.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    const grouped = {
        mejoras: [],
        correcciones: [],
        rendimiento: [],
        seguridad: [],
        otros: [],
    };

    for (const rawLine of lines) {
        if (/^full\s+changelog\s*:/i.test(rawLine)) {
            const match = rawLine.match(/https?:\/\/\S+/i);
            if (match) result.fullChangelogUrl = match[0];
            continue;
        }

        if (/^#{1,6}\s+/i.test(rawLine)) {
            continue;
        }

        const isBullet = /^[-*]\s+/.test(rawLine);
        const cleaned = normalizeLine(rawLine);
        if (!cleaned) continue;

        if (shouldHideForUser(cleaned)) {
            result.hiddenInternalCount += 1;
            continue;
        }

        if (isBullet) {
            const category = getCategory(cleaned);
            grouped[category].push(cleaned);
        } else {
            result.notes.push(cleaned);
        }
    }

    for (const key of Object.keys(grouped)) {
        grouped[key] = [...new Set(grouped[key])];
    }

    result.sections = SECTION_ORDER
        .map((key) => ({ key, title: SECTION_LABELS[key], items: grouped[key].slice(0, 4) }))
        .filter((section) => section.items.length > 0)
        .slice(0, 3);

    result.highlights = result.sections.flatMap((section) => section.items).slice(0, 8);
    result.notes = [...new Set(result.notes)].slice(0, 3);

    return result;
};
